import React from "react";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Stack,
} from "@mui/material";
import { ReceiptResponseDto } from "../../shared/dtos/receipt/ReceiptResponseDto";
import { formatPlainNumber } from "../../utils/appUtils";

interface ReceiptDetailsTabProps {
  receipt: ReceiptResponseDto;
}

const ReceiptDetailsTab: React.FC<ReceiptDetailsTabProps> = ({ receipt }) => {
  const { header, items, payments, taxBreakdowns, charges } = receipt;

  return (
    <Grid container spacing={3}>
      {/* --- LEFT COLUMN: Customer & General Info --- */}
      <Grid size={{ xs: 12, md: 4 }}>
        <Stack spacing={3}>
          <Card
            elevation={0}
            sx={{ border: "1px solid", borderColor: "divider" }}
          >
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
                Customer Details
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Name
              </Typography>
              <Typography
                variant="body1"
                gutterBottom
                sx={{ fontWeight: "500" }}
              >
                {receipt.customerName || "N/A"}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                gutterBottom
                sx={{ mt: 2 }}
              >
                Sale Type
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: "500" }}>
                {receipt.saleType}
              </Typography>
            </CardContent>
          </Card>

          <Card
            elevation={0}
            sx={{ border: "1px solid", borderColor: "divider" }}
          >
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
                Company Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                {header?.companySnapshot?.companyName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                PIN: {header?.companySnapshot?.taxId}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {header?.companySnapshot?.address}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {header?.companySnapshot?.phone} |{" "}
                {header?.companySnapshot?.email}
              </Typography>
            </CardContent>
          </Card>
        </Stack>
      </Grid>

      {/* --- RIGHT COLUMN: Items & Financials --- */}
      <Grid size={{ xs: 12, sm: 8 }}>
        <Card
          elevation={0}
          sx={{ border: "1px solid", borderColor: "divider", mb: 3 }}
        >
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
              Line Items
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead sx={{ bgcolor: "background.default" }}>
                  <TableRow>
                    <TableCell>Description</TableCell>
                    <TableCell align="right">Qty</TableCell>
                    <TableCell align="right">Unit Price</TableCell>
                    <TableCell align="right">Disc/Unit</TableCell>
                    <TableCell align="right">Tax</TableCell>
                    <TableCell align="right">Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items?.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: "500" }}>
                          {item.name}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">{item.qty}</TableCell>
                      <TableCell align="right">
                        {formatPlainNumber(item.price)}
                      </TableCell>
                      <TableCell align="right" sx={{ color: "error.main" }}>
                        {item.discount > 0
                          ? formatPlainNumber(item.discount)
                          : "-"}
                      </TableCell>
                      <TableCell align="right">
                        {formatPlainNumber(item.taxAmount)} ({item.taxCode})
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: "bold" }}>
                        {formatPlainNumber(item.amount)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        {/* Financial Summary & Breakdowns Grid */}
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Card
              elevation={0}
              sx={{
                border: "1px solid",
                borderColor: "divider",
                height: "100%",
              }}
            >
              <CardContent>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ fontWeight: "bold" }}
                >
                  Payments
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableBody>
                      {payments?.map((payment) => (
                        <TableRow key={payment.id}>
                          <TableCell sx={{ borderBottom: "none", pl: 0 }}>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: "bold" }}
                            >
                              {payment.paymentMethodName}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {new Date(payment.createdAt).toLocaleTimeString()}
                            </Typography>
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              borderBottom: "none",
                              pr: 0,
                              fontWeight: "bold",
                              color: "success.main",
                            }}
                          >
                            {formatPlainNumber(payment.amount)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Inclusive / Exclusive Charges Breakdown */}
                {charges && charges.length > 0 && (
                  <Box sx={{ mt: 3 }}>
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      gutterBottom
                    >
                      Applied Charges
                    </Typography>
                    {charges.map((charge, idx) => (
                      <Box
                        key={idx}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography variant="caption">
                          {charge.name} ({charge.rate}% - {charge.chargeType})
                        </Typography>
                        <Typography variant="caption">
                          {formatPlainNumber(charge.amount)}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}

                {/* Tax Breakdown */}
                {taxBreakdowns && taxBreakdowns.length > 0 && (
                  <Box sx={{ mt: charges && charges.length > 0 ? 1 : 3 }}>
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      gutterBottom
                    >
                      Tax Breakdown
                    </Typography>
                    {taxBreakdowns.map((tax) => (
                      <Box
                        key={tax.id}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography variant="caption">{tax.name}</Typography>
                        <Typography variant="caption">
                          {formatPlainNumber(tax.taxAmount)}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <Card
              elevation={0}
              sx={{
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "background.default",
                height: "100%",
              }}
            >
              <CardContent>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ fontWeight: "bold" }}
                >
                  Summary
                </Typography>
                {/* Currency and Exchange Rate */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                    mt: 2,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Currency
                  </Typography>
                  <Typography
                    variant="body2"
                    color="secondary.main"
                    sx={{ fontWeight: "bold" }}
                  >
                    {receipt.currencyCode || "KES"}
                  </Typography>
                </Box>

                {Number(receipt.exchangeRate) !== 1 && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Exchange Rate
                    </Typography>
                    <Typography
                      variant="body2"
                      color="secondary.main"
                      sx={{ fontWeight: "bold" }}
                    >
                      {receipt.exchangeRate}
                    </Typography>
                  </Box>
                )}

                <Divider sx={{ my: 1 }} />
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Subtotal
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: "500" }}>
                    {formatPlainNumber(receipt.subtotal)}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Total Discount
                  </Typography>
                  <Typography
                    variant="body2"
                    color="error.main"
                    sx={{ fontWeight: "500" }}
                  >
                    -{formatPlainNumber(receipt.totalDiscount)}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Total Tax
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: "500" }}>
                    {formatPlainNumber(receipt.totalTax)}
                  </Typography>
                </Box>
                <Divider sx={{ my: 1.5 }} />

                {/* Grand Total - Explcitly denoting Currency */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 2,
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                    Grand Total
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                    {receipt.currencyCode} {formatPlainNumber(receipt.total)}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Amount Paid
                  </Typography>
                  <Typography
                    variant="body2"
                    color="success.main"
                    sx={{ fontWeight: "bold" }}
                  >
                    {formatPlainNumber(receipt.amountPaid)}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="body2" color="text.secondary">
                    Change Due
                  </Typography>
                  <Typography
                    variant="body2"
                    color="error.main"
                    sx={{ fontWeight: "bold" }}
                  >
                    {formatPlainNumber(receipt.changeAmount)}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default ReceiptDetailsTab;
